import { useState } from 'react';
import { hotRankings } from '@/mocks/home';
import { useToast } from '@/components/base/Toast';

const trendMap: Record<string, { icon: string; color: string; text: string }> = {
  up: { icon: 'ri-arrow-up-line', color: 'text-primary-300', text: '上升' },
  down: { icon: 'ri-arrow-down-line', color: 'text-background-400', text: '下降' },
  flat: { icon: 'ri-subtract-line', color: 'text-background-400', text: '持平' },
};

export default function HotRankingSection() {
  const [activeTab, setActiveTab] = useState('today');
  const showToast = useToast();

  const list = hotRankings[activeTab as keyof typeof hotRankings] as {
    rank: number;
    name: string;
    desc: string;
    heat: number;
    trend: string;
  }[];

  const formatHeat = (value: number) => (value >= 10000 ? `${(value / 10000).toFixed(1)}w` : `${value}`);

  return (
    <section id="ranking" className="scroll-mt-24 bg-foreground-950">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div data-reveal className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-primary-400"></span>
              <span className="text-xs font-bold tracking-[0.25em] text-primary-300">04 / 热度榜单</span>
            </div>
            <h2 className="mt-4 font-heading text-2xl font-black leading-tight tracking-tight text-background-50 md:text-4xl">
              谁在领跑？撸撸射入口热度榜
            </h2>
            <p className="mt-3 text-sm leading-7 text-background-300 md:text-base">
              根据站内访问与搜索行为实时统计，越靠前的入口关注度越高。想省时间，直接从榜首开始试。
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-full border border-background-700 bg-foreground-900 p-1">
            {hotRankings.tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-background-300 hover:text-background-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
          {list.map((item, i) => {
            const trend = trendMap[item.trend];
            const isTop = item.rank <= 3;
            return (
              <div
                key={item.name}
                data-reveal
                style={{ transitionDelay: `${i * 50}ms` }}
                className={`flex items-center gap-4 rounded-lg border p-4 transition-colors md:p-5 ${
                  isTop
                    ? 'border-primary-700/60 bg-foreground-900'
                    : 'border-background-800 bg-foreground-900/60'
                }`}
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md font-heading text-lg font-black ${
                    isTop ? 'bg-primary-500 text-foreground-950' : 'bg-background-800 text-background-300'
                  }`}
                >
                  {item.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-base font-bold text-background-50">{item.name}</p>
                  <p className="mt-0.5 truncate text-xs text-background-400">{item.desc}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className={`flex items-center gap-1 text-xs font-semibold ${trend.color}`}>
                    <i className={trend.icon} aria-hidden="true"></i>
                    {trend.text}
                  </span>
                  <span className="font-heading text-sm font-bold text-primary-300">{formatHeat(item.heat)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => showToast(`正在打开榜单入口：${item.name}`)}
                  aria-label={`访问 ${item.name}`}
                  className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-700 text-background-200 transition-colors hover:border-primary-400 hover:text-primary-300"
                >
                  <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}